import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ShopifyRegion } from "../ShopifyGeoSpatialDashboard.data";
import { useLanguage } from "src/hooks/UseLanguage";
import ShopifyCityMapLibre, { countMappableShopifyCities } from "../../maps/ShopifyCityMapLibre";
import {
  type PakistanShapeKey,
  geoNumberText,
  inferPakistanRegionSlug,
} from "./ShopifyGeoSpatialDashboard.utils";
import ShopifyGeoCityMapFallback from "./ShopifyGeoCityMapFallback";
import "../../../styles/ShopifyGeoPakistanCitiesMap.scss";

const ShopifyGeoPakistanCitiesMap: FC<{
  region: ShopifyRegion;
  shapeKey: PakistanShapeKey;
  onCityPinClick?: (_city: string, _regionId: string) => void;
  selectedCityNormalized?: string | null;
  selectedRegionId?: string | null;
  crossFilterCityLabel?: string | null;
}> = ({ region, shapeKey, onCityPinClick, selectedCityNormalized, selectedRegionId, crossFilterCityLabel }) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const regionSlug = inferPakistanRegionSlug(region.id, region.title);
  const mappable = useMemo(() => countMappableShopifyCities(region.cities), [region.cities]);

  const formatRevenue = useCallback(
    (amount: number) =>
      t("shopifyGeo.tooltips.totalSalesAmount", {
        amount: geoNumberText(Math.round(amount).toLocaleString(), isRTL),
      }),
    [isRTL, t],
  );

  if (mappable === 0) {
    return <ShopifyGeoCityMapFallback shapeKey={shapeKey} cities={region.cities} />;
  }

  const mapWrapperAria = crossFilterCityLabel?.trim()
    ? t("shopifyGeo.cityMap.mapAriaWithCity", {
        region: region.title,
        city: crossFilterCityLabel.trim(),
      })
    : t("shopifyGeo.cityMap.mapAria", { region: region.title });

  return (
    <div
      className="shopify-geo__city-map shopify-geo__city-map--maplibre"
      role="img"
      aria-label={mapWrapperAria}
    >
      <ShopifyCityMapLibre
        cities={region.cities}
        shapeKey={shapeKey}
        regionId={regionSlug}
        regionTitle={region.title}
        metricLabel={t("shopifyGeo.cityMap.metricLabel")}
        formatRevenue={formatRevenue}
        onCityPinClick={
          onCityPinClick ? city => onCityPinClick(city, regionSlug) : undefined
        }
        selectedCityNormalized={selectedCityNormalized}
        selectedRegionId={selectedRegionId}
        crossFilterCityLabel={crossFilterCityLabel}
      />
    </div>
  );
};

export default ShopifyGeoPakistanCitiesMap;
